import { Injectable } from '@angular/core';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { CONSTANTS } from 'src/app/components/shared/modal/components/loading-modal/loading-modal.constants';
import { FormatType, OrientationType } from '../../models/reports/pdf.model';
import { CommonUtils } from '../common/common.utils';

@Injectable({
  providedIn: 'root',
})
export class ReportsUtils {
  constructor(private readonly commonUtils: CommonUtils) {}

  /**
   * Used to export pdf report of given parameters
   *
   * @param {*} htmlContent
   * @param {string} reportTitle
   * @param {string} reportFileName
   * @param {OrientationType} [orientation='p']
   * @param {FormatType} [format='letter']
   * @memberof ReportsUtils
   */
  exportPdfReport(
    htmlContent: string,
    reportTitle: string,
    reportFileName: string,
    orientation: OrientationType = 'p',
    format: FormatType = 'letter',
  ) {
    this.commonUtils.updateLoadingModal(true, CONSTANTS.PDF);
    this.commonUtils.lockUpdateLoadingModal(true);
    this.commonUtils.generatePdfFromHtml(
      htmlContent,
      reportTitle,
      reportFileName,
      orientation,
      format,
    );
  }

  /**
   * Used to export CSV report of given parameters
   *
   * @param {(string[][] | null)} [customHeader=null]
   * @param {*} report
   * @param {(string[][] | null)} [customFooter=null]
   * @param {string} fileName
   * @memberof ReportsUtils
   */
  exportCSVReport<T>(
    report: T[],
    fileName: string,
    customHeader: string[][] | null = null,
    customFooter: string[][] | null = null,
    customColumnHeading: string[] | null = null,
  ) {
    const dataToExport: string[][] = [];
    const normalizeObjectFields = this.normalizeObjectFields(report as []);
    const firstRecord = normalizeObjectFields[0];
    const columnHeading = Object.keys(firstRecord).map((i) => {
      return `${i.toUpperCase()}`;
    });

    if (customHeader) {
      dataToExport.push(customColumnHeading ?? columnHeading);
    }

    // write each value to an array, push to array of arrays for worksheet
    normalizeObjectFields.forEach((row: any) => {
      const exportedRow = [];
      for (const key in row) {
        exportedRow.push(row[key]);
      }
      dataToExport.push(exportedRow as string[]);
    });

    if (customFooter) {
      customFooter.forEach((val) => {
        dataToExport.push(val);
      });
    }

    this.commonUtils.downloadCSV(dataToExport, fileName);
    this.commonUtils.updateBannerType(BANNER_TYPE.DOWNLOAD_SUCCESSFUL);
  }

  private normalizeObjectFields<T extends Record<string, any>>(data: T[]): T[] {
    if (!Array.isArray(data) || data.length === 0) return [];

    // Collect all unique keys from all records
    const keys = new Set<string>();
    data.forEach((item) => Object.keys(item).forEach((key) => keys.add(key)));
    // Normalize each record to ensure all keys exist
    return data.map((item) => {
      const newItem = { ...item } as any;
      keys.forEach((key) => {
        if (!(key in newItem) || newItem[key] === null) {
          newItem[key] = '';
        }
      });
      return newItem;
    });
  }
}
